import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useDashboardCurrency } from "@/hooks/useDashboardCurrency";
import { useStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TableSkeleton } from "@/components/skeletons";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PreviewBanner from "@/components/preview-banner";
import {
  Home,
  DollarSign,
  Users,
  Target,
  Eye,
  BarChart3,
  ShieldAlert,
  CheckCircle2,
  ArrowUpDown,
  Filter,
  Check,
  X,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Link } from "wouter";
import ClientCampaignTable from "@/components/client-campaign-table";

interface AccountOverview {
  memberId: string;
  memberEmail: string;
  memberName: string;
  memberRole: string;
  adAccountId: string;
  adAccountName: string;
  spend: number;
  leads: number;
  impressions: number;
  clicks: number;
  reach: number;
  cpl: number;
  ctr: number;
  cpm: number;
  roas: number;
  purchaseValue: number;
  purchases: number;
  activeCampaigns: number;
  alerts: string[];
  alertLevel: "ok" | "warning" | "critical" | "error";
  error?: string;
  businessType?: string;
  clientName?: string | null;
  clientId?: string | null;
  kpiSettings?: any;
  assignedWorkerName?: string | null;
}

interface PreviewMeta {
  enabled: boolean;
  reason: string;
  source: string;
}

const DATE_PRESETS = [
  { value: "today", labelKey: "dashboard.datePresets.today" },
  { value: "yesterday", labelKey: "dashboard.datePresets.yesterday" },
  { value: "last_7d", labelKey: "dashboard.datePresets.last7d" },
  { value: "last_14d", labelKey: "dashboard.datePresets.last14d" },
  { value: "last_30d", labelKey: "dashboard.datePresets.last30d" },
  { value: "this_month", labelKey: "dashboard.datePresets.thisMonth" },
  { value: "last_month", labelKey: "dashboard.datePresets.lastMonth" },
] as const;

const COMPARE_PRESETS = [
  { value: "none", labelKey: "dashboard.comparePresets.none" },
  { value: "previous_period", labelKey: "dashboard.comparePresets.previousPeriod" },
  { value: "last_7d", labelKey: "dashboard.comparePresets.last7d" },
  { value: "last_14d", labelKey: "dashboard.comparePresets.last14d" },
  { value: "last_30d", labelKey: "dashboard.comparePresets.last30d" },
  { value: "last_month", labelKey: "dashboard.comparePresets.lastMonth" },
] as const;

function formatCurrency(value: number, currency = "PLN"): string {
  const locale = currency === "USD" ? "en-US" : currency === "EUR" ? "de-DE" : "pl-PL";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("pl-PL").format(Math.round(value));
}

function calcDelta(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

function DeltaBadge({ delta }: { delta: number | null }) {
  if (delta === null) return null;
  const isPositive = delta >= 0;
  return (
    <span
      className={`text-[10px] font-medium ml-1 ${isPositive ? "text-emerald-400" : "text-red-400"}`}
    >
      {isPositive ? "+" : ""}
      {delta.toFixed(1)}%
    </span>
  );
}

export default function ManagerDashboardPage(
  props: { params?: { [param: string]: string | undefined }; embedded?: boolean } = {}
) {
  const { embedded = false } = props;
  const { t } = useTranslation();
  const { user } = useAuth();
  const dc = useDashboardCurrency();
  const formatCurrency = dc.fmtShort;
  const { setAuthToken } = useStore();
  const [datePreset, setDatePreset] = useState("last_7d");
  const [comparePreset, setComparePreset] = useState("none");
  const [accountFilter, setAccountFilter] = useState("");
  const [filterDraft, setFilterDraft] = useState("");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const forcePreview = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("preview") === "1";
  }, []);

  // Check if currently impersonating (original owner token saved in localStorage)
  const originalOwnerToken = localStorage.getItem("gawronify-original-owner-token");

  const handleRestoreOwner = () => {
    if (!originalOwnerToken) return;
    setAuthToken(originalOwnerToken);
    localStorage.removeItem("gawronify-original-owner-token");
    const origin = localStorage.getItem("gawronify-impersonation-origin") || "/agency-admin";
    localStorage.removeItem("gawronify-impersonation-origin");
    queryClient.clear();
    window.location.href = origin;
  };

  const userRole = (user as any)?.role;
  const isEmployee = userRole === "employee";
  const isManager =
    userRole === "manager" ||
    userRole === "agency_owner" ||
    userRole === "super_admin" ||
    userRole === "employee";

  const requestBody = useMemo(() => {
    const body: any = { datePreset };
    if (forcePreview) body.forcePreview = true;
    if (comparePreset !== "none") body.comparePreset = comparePreset;
    return body;
  }, [datePreset, comparePreset, forcePreview]);

  const { data, isLoading, error } = useQuery<{
    accounts: AccountOverview[];
    totalSpend: number;
    totalLeads: number;
    comparison?: { totalSpend: number; totalLeads: number; accounts: AccountOverview[] } | null;
    preview?: PreviewMeta | null;
  }>({
    queryKey: ["/api/manager/accounts-overview", requestBody],
    queryFn: () => apiRequest("POST", "/api/manager/accounts-overview", requestBody),
    enabled: isManager,
    staleTime: 60_000,
  });

  const accounts = useMemo(() => data?.accounts || [], [data?.accounts]);
  const comparisonData = data?.comparison;
  const previewEnabled = data?.preview?.enabled === true;
  const previewDescription =
    data?.preview?.reason === "token_expired"
      ? t("managerDashboard.previewTokenExpired")
      : t("managerDashboard.previewDefault");

  // Filter accounts by name
  const filteredAccounts = useMemo(() => {
    if (!accountFilter.trim()) return accounts;
    const kw = accountFilter.trim().toLowerCase();
    return accounts.filter(
      (a) =>
        a.adAccountName.toLowerCase().includes(kw) ||
        a.memberName.toLowerCase().includes(kw) ||
        a.memberEmail.toLowerCase().includes(kw)
    );
  }, [accounts, accountFilter]);

  if (!isManager) {
    return (
      <div className="min-h-screen bg-[#030407] flex items-center justify-center">
        <Card className="bg-[#0a0c10] border-white/[0.1]">
          <CardContent className="p-6">
            <h1 className="text-xl font-bold text-white mb-2">{t("managerDashboard.noAccess")}</h1>
            <p className="text-white/60">{t("managerDashboard.noAccessDesc")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const criticalAccounts = filteredAccounts.filter((a) => a.alertLevel === "critical");
  const warningAccounts = filteredAccounts.filter((a) => a.alertLevel === "warning");
  const errorAccounts = filteredAccounts.filter((a) => a.alertLevel === "error");
  const okAccounts = filteredAccounts.filter((a) => a.alertLevel === "ok");

  return (
    <div className="h-full overflow-y-auto bg-[#030407] p-4">
      <div className="max-w-7xl mx-auto min-h-full flex flex-col">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            {originalOwnerToken ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestoreOwner}
                className="border-[#ef3e29]/40 text-[#ef3e29] hover:text-white hover:bg-[#ef3e29]/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("managerDashboard.backToAdmin")}
              </Button>
            ) : !embedded ? (
              <Link href="/dashboard">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/[0.15] text-white/70 hover:text-white hover:bg-white/[0.08]"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {t("managerDashboard.dashboardLink")}
                </Button>
              </Link>
            ) : null}
            <div>
              <h1 className="text-heading-2 font-extrabold text-white mb-2">
                <BarChart3 className="w-6 h-6 inline mr-2 text-[#ef3e29]" />
                {t("managerDashboard.title")}
              </h1>
              <p className="text-white/60 text-sm">
                {isEmployee
                  ? t("managerDashboard.subtitleEmployee")
                  : t("managerDashboard.subtitleManager")}{" "}
                ·{" "}
                {t("managerDashboard.accountsCount", {
                  filtered: filteredAccounts.length,
                  total: accounts.length,
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Controls bar: date, comparison, filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6 flex-shrink-0">
          {/* Date preset */}
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-48 bg-white/[0.06] border-white/[0.12] text-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
              {DATE_PRESETS.map((p) => (
                <SelectItem
                  key={p.value}
                  value={p.value}
                  className="text-white hover:bg-white/[0.08] text-sm"
                >
                  {t(p.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Comparison preset */}
          <Select value={comparePreset} onValueChange={setComparePreset}>
            <SelectTrigger className="w-48 bg-white/[0.06] border-white/[0.12] text-white text-sm">
              <div className="flex items-center gap-1.5">
                <ArrowUpDown className="w-3 h-3 text-white/40" />
                <SelectValue placeholder={t("managerDashboard.comparePlaceholder")} />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
              {COMPARE_PRESETS.map((p) => (
                <SelectItem
                  key={p.value}
                  value={p.value}
                  className="text-white hover:bg-white/[0.08] text-sm"
                >
                  {t(p.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filter button + popover */}
          <Popover
            open={filterPanelOpen}
            onOpenChange={(open) => {
              setFilterPanelOpen(open);
              if (open) setFilterDraft(accountFilter);
            }}
          >
            <PopoverTrigger asChild>
              <button
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  accountFilter
                    ? "bg-[#ef3e29]/10 border-[#ef3e29]/40 text-white"
                    : "bg-white/[0.06] border-white/[0.12] text-white/60 hover:text-white hover:bg-white/[0.08]"
                }`}
              >
                <Filter className="w-4 h-4" />
                {t("managerDashboard.filters")}
                {accountFilter && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#ef3e29] text-white rounded-full">
                    1
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-popover border-border" align="start">
              <div className="p-3 border-b border-white/[0.08]">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Filter className="w-3.5 h-3.5 text-[#ef3e29]" />
                  {t("managerDashboard.filtersAccounts")}
                </h3>
              </div>
              <div className="p-3 space-y-3">
                <Field>
                  <FieldLabel className="text-white/50">
                    {t("managerDashboard.filterByName")}
                  </FieldLabel>
                  <Input
                    placeholder={t("managerDashboard.filterPlaceholder")}
                    value={filterDraft}
                    onChange={(e) => setFilterDraft(e.target.value)}
                    className="bg-white/[0.06] border-white/[0.12] text-white text-sm placeholder:text-white/30 h-9"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setAccountFilter(filterDraft);
                        setFilterPanelOpen(false);
                      }
                    }}
                  />
                  <p className="text-[10px] text-white/30 mt-1">
                    {t("managerDashboard.filterHint")}
                  </p>
                </Field>
              </div>
              <div className="p-3 border-t border-white/[0.08] flex items-center gap-2">
                {accountFilter && (
                  <button
                    onClick={() => {
                      setAccountFilter("");
                      setFilterDraft("");
                      setFilterPanelOpen(false);
                    }}
                    className="px-3 py-1.5 text-xs text-white/50 hover:text-white border border-white/[0.12] rounded-lg transition-colors"
                  >
                    {t("managerDashboard.filterClear")}
                  </button>
                )}
                <div className="flex-1" />
                <button
                  onClick={() => {
                    setAccountFilter(filterDraft);
                    setFilterPanelOpen(false);
                  }}
                  className="px-3 py-1.5 text-xs text-white bg-[#ef3e29] hover:bg-[#ef3e29]/80 rounded-lg transition-colors flex items-center gap-1"
                >
                  <Check className="w-3 h-3" />
                  {t("managerDashboard.filterApply")}
                </button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Active filter chip */}
          {accountFilter && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.06] border border-white/[0.1] rounded-lg">
              <span className="text-xs text-white/50">{t("managerDashboard.filterChipLabel")}</span>
              <span className="text-xs text-white font-medium">{accountFilter}</span>
              <button
                onClick={() => {
                  setAccountFilter("");
                  setFilterDraft("");
                }}
                className="text-white/40 hover:text-white ml-1"
              >
                <X className="w-3 h-3" />
              </button>
              <span className="text-[10px] text-white/30 ml-1">
                {filteredAccounts.length}/{accounts.length}
              </span>
            </div>
          )}

          {/* Comparison label */}
          {comparisonData && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08]">
              <ArrowUpDown className="w-3.5 h-3.5 text-[#ef3e29]" />
              <span className="text-xs text-white/60">
                {t("managerDashboard.comparisonWith")}{" "}
                <span className="text-white/80 font-medium">
                  {t(COMPARE_PRESETS.find((p) => p.value === comparePreset)?.labelKey ?? "", {
                    defaultValue: comparePreset,
                  })}
                </span>
              </span>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        {/* Summary KPI cards — data-dense layout */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-dense mb-6 flex-shrink-0">
          <Card className="bg-[#0a0c10] border-white/[0.1]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-white/60">{t("managerDashboard.totalSpend")}</span>
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-24" />
              ) : (
                <div className="text-xl font-bold text-white">
                  {formatCurrency(data?.totalSpend || 0)}
                  {comparisonData && (
                    <DeltaBadge
                      delta={calcDelta(data?.totalSpend || 0, comparisonData.totalSpend)}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-[#0a0c10] border-white/[0.1]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-white/60">{t("managerDashboard.totalLeads")}</span>
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <div className="text-xl font-bold text-white">
                  {formatNumber(data?.totalLeads || 0)}
                  {comparisonData && (
                    <DeltaBadge
                      delta={calcDelta(data?.totalLeads || 0, comparisonData.totalLeads)}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          <Card
            className={`border-white/[0.1] ${criticalAccounts.length > 0 ? "bg-red-950/40 border-red-500/30" : "bg-[#0a0c10]"}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <ShieldAlert className="w-4 h-4 text-red-400" />
                <span className="text-xs text-white/60">
                  {t("managerDashboard.criticalAlerts")}
                </span>
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-10" />
              ) : (
                <div
                  className={`text-xl font-bold ${criticalAccounts.length > 0 ? "text-red-400" : "text-white"}`}
                >
                  {criticalAccounts.length + errorAccounts.length}
                </div>
              )}
            </CardContent>
          </Card>
          <Card
            className={`border-white/[0.1] ${warningAccounts.length > 0 ? "bg-amber-950/30 border-amber-500/30" : "bg-[#0a0c10]"}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-white/60">{t("managerDashboard.toWatch")}</span>
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-10" />
              ) : (
                <div
                  className={`text-xl font-bold ${warningAccounts.length > 0 ? "text-amber-400" : "text-white"}`}
                >
                  {warningAccounts.length}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-[#0a0c10] border-white/[0.1]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-white/60">{t("managerDashboard.ok")}</span>
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-10" />
              ) : (
                <div className="text-xl font-bold text-emerald-400">{okAccounts.length}</div>
              )}
            </CardContent>
          </Card>
        </div>

        {previewEnabled && (
          <div className="mb-6">
            <PreviewBanner description={previewDescription} />
          </div>
        )}

        {/* Viral Partner Bot - Interactive Chat */}
        {/* REMOVED: ViralPartnerChat replaced with client campaign tables */}

        {/* Loading state — skeleton tabel kampanii pogrupowanych po klientach */}
        {isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <Card key={i} className="bg-[#0a0c10] border-white/[0.1]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                  <TableSkeleton rows={4} columns={6} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Client-grouped campaign tables */}
        {!isLoading && filteredAccounts.length > 0 && (
          <ClientGroupedCampaigns
            accounts={filteredAccounts}
            datePreset={datePreset}
            previewEnabled={previewEnabled}
          />
        )}

        {/* Empty / error state — show setup CTA when no accounts or agency not configured */}
        {!isLoading && (accounts.length === 0 || error) && (
          <div className="flex flex-col items-center justify-center py-16">
            <Users className="w-16 h-16 text-white/20" />
            <h2 className="text-xl font-bold text-white mt-4 mb-2">
              {(error as any)?.message?.includes("404") ||
              (error as any)?.message?.includes("agencji")
                ? t("managerDashboard.emptyConfigureAgency")
                : accounts.length === 0
                  ? t("managerDashboard.emptyAddAccounts")
                  : t("managerDashboard.emptyRefresh")}
            </h2>
            <p className="text-white/60 text-center max-w-md mb-6">
              {(error as any)?.message?.includes("404") ||
              (error as any)?.message?.includes("agencji")
                ? t("managerDashboard.emptyConfigureAgencyDesc")
                : accounts.length === 0
                  ? t("managerDashboard.emptyAddAccountsDesc")
                  : t("managerDashboard.emptyRefreshDesc")}
            </p>
            {((error as any)?.message?.includes("404") ||
              (error as any)?.message?.includes("agencji") ||
              accounts.length === 0) &&
              !isEmployee && (
                <Link href="/agency-admin">
                  <Button className="bg-[#ef3e29] hover:bg-[#d63520] text-white font-medium px-6">
                    {t("managerDashboard.openAgencyPanel")}
                  </Button>
                </Link>
              )}
            {error &&
              !(error as any)?.message?.includes("404") &&
              !(error as any)?.message?.includes("agencji") && (
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="border-white/[0.12] text-white/60 hover:bg-white/[0.06]"
                >
                  {t("managerDashboard.refreshPage")}
                </Button>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Client-grouped campaign view ──────────

interface ClientGroup {
  clientName: string;
  clientId: string | null;
  businessType: string;
  kpiSettings: any;
  adAccounts: AccountOverview[];
  totalSpend: number;
  totalLeads: number;
  totalPurchaseValue: number;
  totalPurchases: number;
  alertLevel: "ok" | "warning" | "critical" | "error";
}

function ClientGroupedCampaigns({
  accounts,
  datePreset,
  previewEnabled = false,
}: {
  accounts: AccountOverview[];
  datePreset: string;
  previewEnabled?: boolean;
}) {
  const { t } = useTranslation();
  const [expandedClients, setExpandedClients] = useState<Set<string>>(() =>
    previewEnabled
      ? new Set(accounts.map((account) => account.clientName || account.adAccountName))
      : new Set()
  );

  // Group accounts by client
  const clientGroups = useMemo(() => {
    const groupMap = new Map<string, ClientGroup>();

    for (const acc of accounts) {
      const key = acc.clientId || acc.adAccountId;
      const existing = groupMap.get(key);

      if (existing) {
        existing.adAccounts.push(acc);
        existing.totalSpend += acc.spend;
        existing.totalLeads += acc.leads;
        existing.totalPurchaseValue += acc.purchaseValue || 0;
        existing.totalPurchases += acc.purchases || 0;
        if (acc.alertLevel === "critical" || acc.alertLevel === "error")
          existing.alertLevel = "critical";
        else if (acc.alertLevel === "warning" && existing.alertLevel === "ok")
          existing.alertLevel = "warning";
      } else {
        groupMap.set(key, {
          clientName: acc.clientName || acc.adAccountName,
          clientId: acc.clientId || null,
          businessType: acc.businessType || "lead_gen",
          kpiSettings: acc.kpiSettings || null,
          adAccounts: [acc],
          totalSpend: acc.spend,
          totalLeads: acc.leads,
          totalPurchaseValue: acc.purchaseValue || 0,
          totalPurchases: acc.purchases || 0,
          alertLevel: acc.alertLevel,
        });
      }
    }

    return Array.from(groupMap.values()).sort((a, b) => b.totalSpend - a.totalSpend);
  }, [accounts]);

  const toggleClient = (clientName: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientName)) next.delete(clientName);
      else next.add(clientName);
      return next;
    });
  };

  return (
    <div className="space-y-3 pb-6">
      {clientGroups.map((group) => {
        const isExpanded = expandedClients.has(group.clientName);
        const alertBorder =
          group.alertLevel === "critical" || group.alertLevel === "error"
            ? "border-red-500/30"
            : group.alertLevel === "warning"
              ? "border-amber-500/30"
              : "border-white/[0.1]";
        const alertBg =
          group.alertLevel === "critical" || group.alertLevel === "error"
            ? "bg-red-950/10"
            : group.alertLevel === "warning"
              ? "bg-amber-950/10"
              : "bg-[#0a0c10]";

        return (
          <Card key={group.clientName} className={`${alertBg} ${alertBorder}`}>
            {/* Client header */}
            <button
              onClick={() => toggleClient(group.clientName)}
              className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors"
            >
              <span className="text-white/40">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="text-white font-semibold text-sm">{group.clientName}</h3>
                  <AlertBadge level={group.alertLevel} />
                </div>
                <p className="text-white/40 text-xs mt-0.5">
                  {group.adAccounts.length}{" "}
                  {group.adAccounts.length === 1
                    ? t("managerDashboard.accountSingular")
                    : t("managerDashboard.accountPlural")}{" "}
                  ·{" "}
                  {group.adAccounts
                    .map((a) => a.assignedWorkerName || a.memberName)
                    .filter((v, i, arr) => arr.indexOf(v) === i)
                    .join(", ")}
                </p>
              </div>
              <ClientMetricsSummary group={group} />
            </button>

            {/* Expanded: campaign tables per ad account */}
            {isExpanded && (
              <div className="border-t border-white/[0.06]">
                {group.adAccounts.map((acc) => (
                  <div
                    key={acc.adAccountId}
                    className="border-b border-white/[0.04] last:border-b-0"
                  >
                    {/* Ad account sub-header (only show if multiple accounts per client) */}
                    {group.adAccounts.length > 1 && (
                      <div className="px-4 py-2 bg-white/[0.02] flex items-center gap-2">
                        <BarChart3 className="w-3.5 h-3.5 text-[#ef3e29]" />
                        <span className="text-xs font-medium text-white/70">
                          {acc.adAccountName}
                        </span>
                        <span className="text-[10px] text-white/30">
                          {t("managerDashboard.campaignCount", { count: acc.activeCampaigns })}
                        </span>
                      </div>
                    )}
                    <div className="px-2">
                      <ClientCampaignTable
                        adAccountId={acc.adAccountId}
                        adAccountName={acc.adAccountName}
                        datePreset={datePreset}
                        readOnly={previewEnabled}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function MetricCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-right">
      <div className="text-[10px] text-white/40 uppercase">{label}</div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  );
}

function ClientMetricsSummary({ group }: { group: ClientGroup }) {
  const { t } = useTranslation();
  const { totalSpend, totalLeads, totalPurchaseValue, totalPurchases, businessType } = group;

  if (businessType === "ecommerce") {
    const roas = totalSpend > 0 && totalPurchaseValue > 0 ? totalPurchaseValue / totalSpend : null;
    const cpa = totalPurchases > 0 ? totalSpend / totalPurchases : null;
    return (
      <div className="flex items-center gap-6 text-right">
        <MetricCell
          label={t("managerDashboard.metrics.spend")}
          value={formatCurrency(totalSpend)}
        />
        <MetricCell
          label={t("managerDashboard.metrics.revenue")}
          value={totalPurchaseValue > 0 ? formatCurrency(totalPurchaseValue) : "—"}
        />
        <MetricCell
          label={t("managerDashboard.metrics.roas")}
          value={roas !== null ? roas.toFixed(2) + "x" : "—"}
        />
        <MetricCell
          label={t("managerDashboard.metrics.cpa")}
          value={cpa !== null ? formatCurrency(cpa) : "—"}
        />
      </div>
    );
  }

  if (businessType === "saas") {
    // SaaS: show spend + leads-as-trials + cost per trial
    const costPerTrial = totalLeads > 0 ? totalSpend / totalLeads : null;
    return (
      <div className="flex items-center gap-6 text-right">
        <MetricCell
          label={t("managerDashboard.metrics.spend")}
          value={formatCurrency(totalSpend)}
        />
        <MetricCell label={t("managerDashboard.metrics.trials")} value={formatNumber(totalLeads)} />
        <MetricCell
          label={t("managerDashboard.metrics.costPerTrial")}
          value={costPerTrial !== null ? formatCurrency(costPerTrial) : "—"}
        />
      </div>
    );
  }

  if (businessType === "autowebinar_book_a_call" || businessType === "autowebinar_mid_ticket") {
    // Registrations = leads from Meta; attendees from EverWebinar (not yet available → show '—')
    const costPerRegistration = totalLeads > 0 ? totalSpend / totalLeads : null;
    return (
      <div className="flex items-center gap-6 text-right">
        <MetricCell
          label={t("managerDashboard.metrics.spend")}
          value={formatCurrency(totalSpend)}
        />
        <MetricCell
          label={t("managerDashboard.metrics.registrations")}
          value={formatNumber(totalLeads)}
        />
        <MetricCell
          label={t("managerDashboard.metrics.costPerRegistration")}
          value={costPerRegistration !== null ? formatCurrency(costPerRegistration) : "—"}
        />
        <MetricCell label={t("managerDashboard.metrics.showUp")} value="—" />
        <MetricCell label={t("managerDashboard.metrics.costPerAttendee")} value="—" />
      </div>
    );
  }

  // Default: lead_gen / vsl_funnel
  const cpl = totalLeads > 0 ? totalSpend / totalLeads : null;
  return (
    <div className="flex items-center gap-6 text-right">
      <MetricCell label={t("managerDashboard.metrics.spend")} value={formatCurrency(totalSpend)} />
      <MetricCell label={t("managerDashboard.metrics.leads")} value={formatNumber(totalLeads)} />
      <MetricCell
        label={t("managerDashboard.metrics.cpl")}
        value={cpl !== null ? formatCurrency(cpl) : "—"}
      />
    </div>
  );
}

function AlertBadge({ level }: { level: string }) {
  const { t } = useTranslation();
  const config: Record<string, { labelKey: string; className: string }> = {
    critical: {
      labelKey: "managerDashboard.alerts.critical",
      className: "bg-red-600 text-white animate-pulse",
    },
    error: { labelKey: "managerDashboard.alerts.error", className: "bg-red-800 text-white" },
    warning: { labelKey: "managerDashboard.alerts.warning", className: "bg-amber-600 text-white" },
    ok: {
      labelKey: "managerDashboard.alerts.ok",
      className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    },
  };
  const { labelKey, className } = config[level] || config.ok;
  return <Badge className={`${className} text-[10px] px-1.5 py-0`}>{t(labelKey)}</Badge>;
}
